/**
 * JWT Utilities
 * Extracted from authService.ts
 * Handles JWT token decoding, validation, and expiration checking
 */

import { log } from "@/shared/utils/logger";

/**
 * Decode JWT token to extract payload
 * Returns null if decoding fails
 */
export function decodeToken(token: string): Record<string, unknown> | null {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map(function (c) {
          return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join("")
    );
    const payload: unknown = JSON.parse(jsonPayload);
    return payload !== null && typeof payload === 'object' && !Array.isArray(payload) ? payload as Record<string, unknown> : null;
  } catch {
    return null;
  }
}

/**
 * Check if token is expired or expires soon (within 1 minute)
 * Returns true if expired or about to expire
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded || typeof decoded.exp !== 'number' || !Number.isFinite(decoded.exp) || decoded.exp <= 0) return true;

  const expiryTime = decoded.exp * 1000;
  const currentTime = Date.now();
  const timeUntilExpiry = expiryTime - currentTime;

  if (timeUntilExpiry <= 0) return true;

  const issuedAt =
    typeof decoded.iat === "number" ? decoded.iat * 1000 : undefined;
  const tokenLifetime =
    issuedAt && expiryTime > issuedAt ? expiryTime - issuedAt : undefined;

  // Use a bounded refresh buffer so very short-lived test/dev tokens do not
  // become "expired" immediately after refresh. Normal 15-minute tokens still
  // keep the proactive 60-second renewal window.
  const refreshBufferMs = tokenLifetime
    ? Math.min(60_000, Math.max(5_000, Math.floor(tokenLifetime * 0.1)))
    : 60_000;

  return timeUntilExpiry <= refreshBufferMs;
}

/**
 * Validate JWT token format and structure
 * Checks for proper format, decodability, and required fields
 */
export function validateTokenFormat(token: string): boolean {
  if (!token || typeof token !== "string") {
    log.error("jwtUtils", "Token is not a string");
    return false;
  }

  const parts = token.split(".");
  if (parts.length !== 3) {
    log.error("jwtUtils", "Token does not have 3 parts");
    return false;
  }

  const decoded = decodeToken(token);
  if (!decoded) {
    log.error("jwtUtils", "Token cannot be decoded");
    return false;
  }

  // Check for required JWT fields
  if (typeof decoded.sub !== 'string' || !decoded.sub || typeof decoded.exp !== 'number' || !Number.isFinite(decoded.exp) || decoded.exp <= 0) {
    log.error("jwtUtils", "Token missing required fields");
    return false;
  }

  return true;
}

/**
 * Get time until token expiration in seconds
 * Returns null if token is invalid or already expired
 */
export function getTokenExpiresIn(token: string): number | null {
  const decoded = decodeToken(token);
  if (!decoded || typeof decoded.exp !== 'number' || !Number.isFinite(decoded.exp) || decoded.exp <= 0) return null;

  const expiryTime = decoded.exp * 1000;
  const currentTime = Date.now();
  const timeUntilExpiry = expiryTime - currentTime;

  if (timeUntilExpiry <= 0) return null;

  return Math.floor(timeUntilExpiry / 1000); // Return seconds
}
